import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { AfterViewChecked, OnChanges, SimpleChanges } from '@angular/core';
import { Component, ElementRef, EventEmitter, Input, Output, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { LoadingIndicatorComponent } from '../../loading-indicator/loading-indicator.component';
import type { VisionSelectOption } from './vision-select.models';

@Component({
  selector: 'vision-select',
  templateUrl: './vision-select.component.html',
  styleUrls: ['./vision-select.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule, LoadingIndicatorComponent],
})
export class VisionSelectComponent implements OnChanges, AfterViewChecked {
  @Input() extendClassStyles = null; // Add additional class styles to the select element
  // Options. NOTE: the native-value sync (syncNativeValue) runs on ngOnChanges, so
  // consumers must REASSIGN this (a new array reference) to change options — an
  // in-place push/splice won't fire ngOnChanges and the <select> would silently
  // desync from the model. All current consumers reassign.
  @Input() data: VisionSelectOption[] = []; // Array of objects with value and label
  @Input() formControl: FormControl; // Form control for the select element
  @Input() placeholder = 'Select an option'; // Placeholder text for the select element
  @Input() defaultSelectedOption = ''; // Default selected option
  @Input() disabled = false; // Disable the select element
  @Input() isLoadingState = false; // Loading state for the select element
  @Input() isLoadingPlaceholder = null; // Placeholder text to display when loading
  @Output() changeEvent = new EventEmitter<string>(); // Event emitter for value changes
  @Input() label = 'Select an option'; // Label for the select element
  @Input() selectId = 'select'; // ID for the select element

  @ViewChild('selectRef') private selectRef?: ElementRef<HTMLSelectElement>;

  faCaretDown = faCaretDown; // Icon for the select element

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  // Set when `data`/`defaultSelectedOption` change so the native <select> value is
  // re-synced ONCE after the options re-render (see syncNativeValue).
  private needsValueSync = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['defaultSelectedOption']) {
      this.needsValueSync = true;
    }
  }

  ngAfterViewChecked(): void {
    this.syncNativeValue();
  }

  /**
   * Reflects `defaultSelectedOption` onto the native <select>'s value once after the
   * options (re)render. Without this, replacing `data` at runtime and dropping the
   * previously-selected value makes the browser silently auto-select the first
   * enabled option WITHOUT firing `change`, desyncing the DOM from the parent's
   * model (e.g. the product-finder cascade stalls). Setting value to '' re-selects
   * the disabled placeholder. Guarded by the dirty flag so it runs once per
   * option/default change and never overrides a user's in-DOM pick that the model
   * intentionally lags.
   *
   * Falls back to the placeholder ('') when `defaultSelectedOption` has no matching
   * option — otherwise the native <select> renders BLANK (e.g. geolocation resolves
   * a state the finder doesn't offer), instead of showing the placeholder.
   */
  private syncNativeValue(): void {
    if (!this.needsValueSync || !this.isBrowser || !this.selectRef) {
      return;
    }
    this.needsValueSync = false;
    const target = this.defaultSelectedOption != null ? String(this.defaultSelectedOption) : '';
    const matchesOption = this.data?.some((option) => String(option.value) === target) ?? false;
    this.selectRef.nativeElement.value = matchesOption ? target : '';
  }

  /**
   * Handles the change event of the vision select component.
   * Emits the selected value through the changeEvent output.
   *
   * @param event - The change event object.
   */
  handleChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.changeEvent.emit(value);
  }
}
